import { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import Sidebar, { SECTIONS, getRouteSection } from '@/components/Sidebar'
import { NavUIProvider } from '@/lib/navContext'
import type { User, LeaderboardList, CasinoList, UserPreferences, Market, EventSummary } from '@/lib/api'
import {
  getMe,
  getUserPreferences,
  getLeaderboardList,
  getCasinoList,
  getMarkets,
  getEvents,
} from '@/lib/api'
import { applyRuntime } from './runtime'

// The embedded sidebar widget (epic #33): the SAME src/components/Sidebar.tsx the core app
// renders, bundled standalone so the casino mounts identical navigation — activity bar, hover
// panel, pinned items, keyboard shortcuts — with no drift. Next-only imports are rewritten to
// the shared widget shims (see scripts/build-widget.mjs). Output: public/widget/sidebar.{js,css}.

interface SidebarData {
  user: User
  prefs: UserPreferences
  leaderboardList: LeaderboardList
  casinoList: CasinoList
  markets: Market[]
  events: EventSummary[]
}


// The activity bar's column, rendered before data arrives so it paints with the page. Matches
// the real bar's desktop markup (Sidebar.tsx) exactly — including the active-section highlight,
// computed from the current path — so the interactive bar takes over with no flash or shift.
function ActivityBarSkeleton() {
  const active =
    typeof window !== 'undefined' ? getRouteSection(window.location.pathname) : null
  return (
    <div className="hidden md:flex fixed top-0 left-0 h-full z-50" aria-hidden="true">
      <div className="w-12 h-full bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex flex-col items-center py-3 gap-0.5">
        {SECTIONS.map(s => (
          <div
            key={s.key}
            className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg ${
              s.key === active ? 'bg-white dark:bg-gray-800 shadow-sm' : 'text-gray-400 dark:text-gray-600'
            }`}
          >
            {s.icon}
          </div>
        ))}
        <div className="mt-auto w-9 h-9 rounded-lg flex items-center justify-center text-lg text-gray-400 dark:text-gray-600">
          🚪
        </div>
      </div>
    </div>
  )
}

function SidebarWidget() {
  const [data, setData] = useState<SidebarData | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      getMe(),
      getUserPreferences(),
      getLeaderboardList(),
      getCasinoList(),
      getMarkets(),
      getEvents(),
    ]).then(([user, prefs, leaderboardList, casinoList, markets, events]) => {
      if (cancelled || !user) return
      setData({
        user: user as User,
        prefs: prefs as UserPreferences,
        leaderboardList: leaderboardList as LeaderboardList,
        casinoList: casinoList as CasinoList,
        markets: markets as Market[],
        events: events as EventSummary[],
      })
    })
    return () => {
      cancelled = true
    }
  }, [])

  // While the data loads, paint the static activity bar immediately (its section icons aren't
  // data-dependent). It's pixel-identical to the real bar's column, so the interactive Sidebar
  // swaps in with no shift — the bar appears with the page instead of popping in after the fetch.
  if (!data) return <ActivityBarSkeleton />

  return (
    <NavUIProvider initialPinnedMarkets={data.prefs.pinned_markets}>
      <Sidebar
        username={data.user.discord_username}
        leaderboardList={data.leaderboardList}
        casinoList={data.casinoList}
        initialPrefs={data.prefs}
        markets={data.markets}
        events={data.events}
      />
    </NavUIProvider>
  )
}

function mount() {
  const el = document.getElementById('weeabets-sidebar')
  if (!el) return

  applyRuntime(el)
  createRoot(el).render(<SidebarWidget />)
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount)
  } else {
    mount()
  }
}
